import React from 'react';
import { SkeletonLine, Typography } from '@one-thd/sui-atomic-components';
import { dateFormat, formatDateTime } from '../../order-details/util/format';
import { weekDay, weekDayMonthDay } from '../../../constants/dateStrings';
import ILineItem from '../../../models/line-item';
import { IDeliveryTrackerData } from '../../../models/common';
import { IFulfillmentGroup } from '../../../models/order-details';

const getOrderStatus = (lineItems: ILineItem[]) => {
  const itemStatus = lineItems?.find(
    (item: ILineItem) => item?.statusDescription !== 'Canceled',
  );
  return itemStatus
    ? itemStatus?.statusDescription
    : lineItems?.[0]?.statusDescription;
};

export interface IODOrderStatus {
  deliveryTrackerData: IDeliveryTrackerData | null | undefined;
  deliveryTrackerLoaded?: boolean | undefined;
  fulfillmentGroup: IFulfillmentGroup | undefined;
  fulfillmentType: string;
  salesDate: string | undefined;
}
export const ODOrderStatus = ({
  deliveryTrackerData,
  fulfillmentGroup,
  deliveryTrackerLoaded,
  salesDate,
}: IODOrderStatus) => {
  const fulfillmentType = fulfillmentGroup?.fulfillmentType ?? '';
  const status = getOrderStatus(fulfillmentGroup?.lineItems ?? []);

  const getDeliveryTrackerStatus = () => {
    const statusDisplayText: { [key: string]: string } = {
      ARRIVED: 'Arrived',
      AWAITING_TRUCKLOAD: 'Awaiting Truckload',
      DELIVERED: `Delivered ${dateFormat(
        deliveryTrackerData?.estimatedDeliveryWindow?.date ?? '',
        weekDayMonthDay,
      )}`,
      DELIVERED_WITH_EXCEPTION: 'Delivered with Exception',
      IN_TRANSIT: 'In Transit',
      MISSED_WITH_ATTEMPT: 'Delivery Missed',
      MISSED_WITHOUT_ATTEMPT: 'Delivery Missed without Attempt',
      MISSED_WITHOUT_ENROUTE: 'Delivery Missed without Enroute',
      ON_THE_WAY: 'On the Way',
      ON_VEHICLE_FOR_DELIVERY: 'On Vehicle for Delivery',
      OUT_FOR_DELIVERY: 'Out for Delivery',
      SCHEDULED: 'Scheduled',
      SHIPPED: `Arriving ${dateFormat(
        deliveryTrackerData?.estimatedDeliveryWindow?.date ?? '',
        weekDayMonthDay,
      )}`,
      UNDELIVERABLE: 'Undeliverable',
      PICKED_UP: 'Picked Up',
      ORDER_RECEIVED:
        status === 'On Schedule' && Boolean(deliveryTrackerData?.deliveryWindow)
          ? `Delivery Window: ${formatDateTime(
              deliveryTrackerData?.deliveryWindow?.date || '',
              deliveryTrackerData?.deliveryWindow?.startTime ?? '',
              deliveryTrackerData?.deliveryWindow?.endTime ?? '',
            )}`
          : `Order Received ${dateFormat(
              fulfillmentGroup?.fulfillmentDate || salesDate || '',
              weekDayMonthDay,
            )}`,
      READY_FOR_PICKUP: `Ready for Pickup ${
        deliveryTrackerData?.pickUpDetails?.estimatedPickupDate ?? ''
      }`,
      DELAYED: 'Delayed',
    };
    const deliveryStatusText =
      statusDisplayText?.[
        (deliveryTrackerData as IDeliveryTrackerData)?.status
      ] ?? null;
    return deliveryStatusText ?? '';
  };

  const statusText = () => {
    if (fulfillmentType === 'unavailable') {
      return 'Unavailable';
    }

    if (fulfillmentGroup?.fulfillmentType === 'Additional Items') {
      return `Purchased ${dateFormat(
        fulfillmentGroup?.lineItems?.[0]?.salesDate ?? salesDate ?? '',
        weekDayMonthDay,
      )}`;
    }

    if (status === 'Canceled') {
      return `Canceled ${dateFormat(
        fulfillmentGroup?.lineItems?.[0]?.statusUpdatedDate ?? '',
        weekDayMonthDay,
      )}`;
    }

    if (
      fulfillmentGroup?.lineItems?.[0]?.statusDescription === 'Being Processed'
    ) {
      if (
        fulfillmentType === 'BuyOnlineDeliverFromStore' ||
        fulfillmentType === 'DFS'
      ) {
        const bodfsDeliveryText =
          fulfillmentGroup?.bodfsDeliveryAndHandling?.bodfsDeliveries?.[0]
            ?.reservationWindowDisplayText || '';
        if (bodfsDeliveryText) {
          return `Estimated Arrival ${bodfsDeliveryText}`;
        }
      }
      if (fulfillmentType === 'Appliance') {
        const estimatedDeliveryDate =
          fulfillmentGroup?.shippingAndHandling?.applianceDeliveries?.[0]
            ?.currentDeliveryDate ||
          fulfillmentGroup?.shippingAndHandling?.applianceDeliveries?.[0]
            ?.originalDeliveryDate ||
          fulfillmentGroup?.shippingAndHandling?.estEndDeliveryDate;
        if (estimatedDeliveryDate) {
          return `Estimated Arrival ${dateFormat(
            estimatedDeliveryDate,
            weekDayMonthDay,
          )}`;
        }
      }
      if (
        ((fulfillmentGroup?.shippingAndHandling?.applianceDeliveries?.length ??
          0) > 0 &&
          fulfillmentGroup?.shippingAndHandling?.applianceDeliveries?.[0]
            ?.currentDeliveryDate === undefined) ||
        (fulfillmentGroup?.shippingAndHandling !== undefined &&
          fulfillmentGroup?.shippingAndHandling?.estEndDeliveryDate ===
            undefined)
      ) {
        return 'Delivery Date Pending';
      }
    }

    if (fulfillmentType === 'MISC') {
      return 'Miscellaneous';
    }

    if (deliveryTrackerData?.status) {
      return getDeliveryTrackerStatus();
    }

    if (
      status === 'Shipped' &&
      (fulfillmentGroup?.shippingAndHandling?.shipments?.shipment?.length ??
        0) > 0
    ) {
      return `Arriving ${dateFormat(
        fulfillmentGroup?.shippingAndHandling?.shipments?.shipment?.[0]
          ?.estimatedDeliveryDate ?? '',
        weekDayMonthDay,
      )}`;
    }

    if (status === 'On Schedule') {
      if (fulfillmentGroup?.bodfsDeliveryAndHandling?.bodfsDeliveries?.length) {
        const bodfsDelivery =
          fulfillmentGroup.bodfsDeliveryAndHandling.bodfsDeliveries?.[0];
        const {
          reservationWindowStartTime: startDate,
          reservationWindowDisplayText: preformattedText,
        } = bodfsDelivery;
        const day = dateFormat(startDate ?? '', weekDay);
        const bodfsDeliveryText = `${day}, ${preformattedText}`;
        return `Delivery Window: ${bodfsDeliveryText ?? ''}`;
      }
      return 'Delivery Date Pending';
    }

    if (status === 'Delivered') {
      if (
        (fulfillmentGroup?.lineItems?.[0]?.deliveredDateArray?.length ?? 0) > 0
      ) {
        return `Delivered ${dateFormat(
          fulfillmentGroup?.lineItems?.[0]?.deliveredDateArray?.[0] ?? '',
          weekDayMonthDay,
        )}`;
      }
    }
    return status;
  };

  return deliveryTrackerLoaded ? (
    <Typography variant="body-base" weight="bold">
      {statusText()}
    </Typography>
  ) : (
    <div className="sui-w-[250px] md:sui-w-[500px]">
      <SkeletonLine variant="heading" />
    </div>
  );
};
